import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  SelectOption,
  IconPlus,
  IconSearch,
  OrangeButton,
  IconFilter,
  IconSmallArrow,
  SmallButton,
  IconFormMinus,
  IconFormPlus,
  CustomTable,
  IconReset,
  DownloadClientsCSV,
} from 'components';
import SlhSelect from 'components/slhSelect';
import { COLORS } from 'utils/colors';
import { renderBackendCalendarDate } from 'utils/helpers';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { endpoints } from 'api/endpoints';
import classNames from 'classnames';
import { getRequestData } from 'api/api';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CLIENT_STATUS, ROLE_TYPES } from 'api/constants';
import { Input } from 'antd';

export const AdminClients = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { parentId } = location.state || {};
  const [clientsList, setClientsList] = useState();
  const [allPartners, setAllPartners] = useState();
  const isInitialPartnerApplied = useRef(false);

  const selectedStatus = searchParams.get('status') || null;
  const selectedForms = searchParams.get('forms')
    ? searchParams
        .get('forms')
        .split(',')
        .filter(Boolean)
    : [];
  const selectedPartner = searchParams.get('partner') || '';
  const searchWord = searchParams.get('search') || '';

  const formsFilterOptions = [
    {
      label: <p className="text-14 px-4 py-1.5 rounded-md">Overdue</p>,
      value: 'overdue',
    },
    {
      label: <p className="text-14 px-4 py-1.5 rounded-md">In Grace</p>,
      value: 'in_grace',
    },
    {
      label: <p className="text-14 px-4 py-1.5 rounded-md">All Completed</p>,
      value: 'completed',
    },
  ];

  const buildClientFilters = useCallback((status, partner_id, searchQuery, forms, page) => {
    const filters = {};
    if (status) filters.status = status;
    if (partner_id) filters.partner_ids = partner_id;
    if (searchQuery) filters.search = searchQuery;
    if (forms?.length) filters.forms = forms;
    if (page) filters.page = page;

    return filters;
  }, []);

  const updateQueryParams = useCallback(
    (updates, options = {}) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });

      setSearchParams(next, options);
    },
    [searchParams, setSearchParams],
  );

  const columns = [
    {
      title: 'Resident',
      dataIndex: 'client',
      width: 300,
      render: (_, record) => {
        const icon = () => {
          if (record.hasOverdue) {
            return <IconFormMinus className="min-w-4 min-h-4" color={COLORS.red} />;
          } else if (record.isInGrace) {
            return <IconFormMinus className="min-w-4 min-h-4" color={COLORS.orange} />;
          } else if (record.isCompleted) {
            return <IconFormPlus className="min-w-4 min-h-4" color={COLORS.green} />;
          }
          return <IconFormMinus className="min-w-4 min-h-4" color={COLORS.lightBlue} />;
        };

        return (
          <div className="flex items-center gap-x-1">
            <img
              className="w-[30px] h-[30px] rounded-full"
              src={record.clientPhoto ? record.clientPhoto : DefaultAvatar}
              alt="Resident"
            />
            <p className="text-14 ml-1">{record.client}</p>
            {icon()}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status, record) => {
        const statusAbbr = status.slice(0, 1).toUpperCase();
        return (
          <div className="flex items-center gap-x-3">
            <p className="w-6 h-6 rounded-full bg-white shadow-block-shadow flex justify-center items-center">
              <span
                className={classNames(
                  'uppercase text-xl leading-5 font-medium',
                  { 'text-blue': statusAbbr === 'N' },
                  { 'text-orange': statusAbbr === 'I' },
                  { 'text-green': statusAbbr === 'C' },
                  { 'text-red': statusAbbr === 'R' },
                )}
              >
                {statusAbbr === 'R' ? 'E' : statusAbbr}
              </span>
            </p>
            <p className="text-14 uppercase font-medium">W{record.week ? record.week : 0}</p>
          </div>
        );
      },
    },
    {
      title: 'SLH',
      width: 300,
      dataIndex: 'partner',
      render: (_, record) => (
        <Link
          to={`/partner/view/${record.partnerID}`}
          className="text-14 font-medium text-ellipsis w-[300px] overflow-hidden whitespace-nowrap pr-4"
        >
          {record.partner}
        </Link>
      ),
    },
    {
      title: 'Scholarship Start Date',
      dataIndex: 'startDate',
      width: 120,
      showSorterTooltip: {
        target: 'sorter-icon',
      },
      render: (text) => renderBackendCalendarDate(text),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      width: 120,
      showSorterTooltip: {
        target: 'sorter-icon',
      },
      render: (text) => renderBackendCalendarDate(text),
    },
    {
      title: 'Action',
      width: 100,
      dataIndex: 'action',
      className: 'action',
      render: (_, record) => (
        <SmallButton onClick={() => navigate(`/client/${record.userID}`)} label="Details" />
      ),
    },
  ];

  const fetchClients = useCallback(async (params) => {
    const { data } = await getRequestData(endpoints.getClients(params), ROLE_TYPES.admin);
    if (data) {
      const results = Array.isArray(data.results) ? data.results : [];
      const clientsData = {
        currentPage: data.currentPage,
        pageCount: data.pageCount,
        pageSize: data.pageSize,
        clients: results.map((client) => ({
          client: `${client.firstname} ${client.lastname}`,
          clientID: client.client_id,
          clientPhoto: client.avatar,
          status: client.status,
          partner: client.partner,
          partnerID: client.partner_id,
          startDate: client.start_date,
          endDate: client.end_date,
          userID: client.user_id,
          week: client.week,
          hasOverdue: client.has_overdue,
          isInGrace: client.is_in_grace,
          isCompleted: client.is_completed,
        })),
      };
      setClientsList(clientsData);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await getRequestData(endpoints.getAllPartners(), ROLE_TYPES.admin);
      if (data) {
        setAllPartners(data);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isInitialPartnerApplied.current && !searchParams.get('partner') && parentId) {
      updateQueryParams({ partner: parentId, page: null }, { replace: true });
    }
    isInitialPartnerApplied.current = true;
  }, [parentId, searchParams, updateQueryParams]);

  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const filters = buildClientFilters(
      searchParams.get('status') || null,
      searchParams.get('partner') || '',
      searchParams.get('search') || '',
      searchParams.get('forms')
        ? searchParams
            .get('forms')
            .split(',')
            .filter(Boolean)
        : [],
      page,
    );
    fetchClients(filters);
  }, [buildClientFilters, fetchClients, searchParams]);

  const handlePaginationChange = (page) => {
    updateQueryParams({ page });
  };

  const handleStatusSelectChange = (status) => {
    updateQueryParams({ status, page: null });
  };

  const handleFormsSelectChange = (forms) => {
    const normalizedForms = Array.isArray(forms) ? forms : forms ? [forms] : [];
    updateQueryParams({ forms: normalizedForms.length ? normalizedForms.join(',') : null, page: null });
  };

  const handleSLHSelectChange = (id) => {
    updateQueryParams({ partner: id, page: null });
  };

  const handleFiltersReset = () => {
    setSearchParams(new URLSearchParams());
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    updateQueryParams(
      {
        search: searchTerm || null,
        page: null,
      },
      { replace: true },
    );
  };

  return (
    <div className="flex flex-col w-full gap-y-6">
      <div className="flex justify-between w-full">
        <p className="nunito-title">Residents</p>
        <div className="flex items-center gap-x-6">
          <Input
            className="search w-[300px] text-lightBlue text-16 rounded-ten bg-light"
            type="text"
            value={searchWord}
            placeholder="Search"
            addonBefore={<IconSearch color={COLORS.lightBlue} />}
            onChange={handleSearch}
          />
          <OrangeButton
            onClick={() => navigate('/create-client')}
            label="Add new resident"
            icon={<IconPlus />}
          />
          {clientsList && <DownloadClientsCSV list={clientsList.clients} />}
        </div>
      </div>
      {clientsList && (
        <>
          <div className="flex items-center gap-x-3">
            <div className="flex items-center gap-x-1 mr-3">
              <IconFilter />
              <p className="text-16">Filter</p>
            </div>
            <SelectOption
              onSelect={(value) => handleStatusSelectChange(value)}
              dropdownStyle={{ padding: '16px', background: 'white' }}
              required
              value={selectedStatus}
              suffixIcon={
                <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
              }
              className="h-[40px] text-base font-medium base-block rounded-lg"
              popupClassName="min-w-[220px]"
              placeholder="Status"
              dropdownRender={(originalNode) => originalNode}
              optionRender={(option) => (
                <div
                  className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${selectedStatus === option.value ? 'border-orange' : ''}`}
                >
                  <span
                    className={`w-6 h-6  rounded-full border border-orange ${selectedStatus === option.value ? 'border-[6px]' : ''}`}
                  />
                  <p className="font-14 flex-1">{option.label}</p>
                </div>
              )}
              options={[
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">In Progress</p>,
                  value: CLIENT_STATUS.in_progress,
                },
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">Completed</p>,
                  value: CLIENT_STATUS.completed,
                },
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">Ended</p>,
                  value: CLIENT_STATUS.failed,
                },
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">Not Started</p>,
                  value: CLIENT_STATUS.not_started,
                },
              ]}
            />
            <SelectOption
              onChange={handleFormsSelectChange}
              dropdownStyle={{ padding: '16px', background: 'white' }}
              value={selectedForms}
              suffixIcon={
                <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
              }
              className="h-[40px] text-base font-medium base-block rounded-lg"
              popupClassName="min-w-[260px]"
              placeholder="Forms"
              dropdownRender={(originalNode) => originalNode}
              optionRender={(option) => (
                <div
                  className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${selectedForms.includes(option.value) ? 'border-orange' : ''}`}
                >
                  <span
                    className={`w-6 h-6  rounded-full border border-orange ${selectedForms.includes(option.value) ? 'border-[6px]' : ''}`}
                  />
                  <p className="font-14 flex-1">{option.label}</p>
                </div>
              )}
              options={formsFilterOptions}
            />
            <SlhSelect
              data={allPartners}
              value={selectedPartner}
              onChange={(id) => handleSLHSelectChange(id)}
            />
            {(selectedPartner || selectedStatus || searchWord || selectedForms.length > 0) && (
              <SmallButton onClick={handleFiltersReset} label="Reset All" icon={<IconReset />} />
            )}
          </div>
          <CustomTable
            columns={columns}
            data={clientsList.clients}
            pageCount={clientsList.pageCount}
            current={clientsList.currentPage}
            pageSize={clientsList.pageSize}
            onChange={handlePaginationChange}
            rowKey={(record) => record.userID}
          />
        </>
      )}
    </div>
  );
};
